import { useEffect, useRef } from 'react';
import { type Components, Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import EmptyLayout from '@/components/layouts/empty-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import { SessionListItem } from '@/components/sessions/session-list/session-list-item';
import useConversationSessions from '@/hooks/sessions/use-conversation-sessions';
import { useIsDesktop } from '@/hooks/use-user-platform';
import type { ConversationSessionsTypeUnion } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { clearLastPosition, useConversationSessionStore } from '@/stores/conversation-session';
import { generateSessionKey } from '@/utils/session';

const components: Components<ConversationSessionsTypeUnion> = {
  Item: ({ children, ...props }) => (
    <>
      <div
        className='group mt-1 desktop:px-5'
        {...props}>
        {children}
      </div>
      <div className='border-b mt-1 border-base-100 mx-4' />
    </>
  ),
  EmptyPlaceholder: () => <EmptyLayout label='conversation.list.no-recent' />,
};

export default function SessionList() {
  const listRef = useRef<VirtuosoHandle>(null);
  const { availableSessions, isLoading } = useConversationSessions();
  const lastPosition = useConversationSessionStore.getState().lastPositions[ROUTES.MESSAGES.$path()];

  const isDesktop = useIsDesktop();

  const handleKeepListPosition = () => {
    listRef.current?.getState((state) => {
      useConversationSessionStore.getState().setLastPosition(ROUTES.MESSAGES.$path(), { ...state, anchorId: 0 });
    });
  };
  useEffect(() => {
    clearLastPosition(undefined, { keepMainListPosition: true });
  }, []);

  return (
    <LoadingLayout promise={isLoading}>
      {() => (
        <Virtuoso
          ref={listRef}
          className='flex flex-1 flex-col items-stretch desktop:!overflow-y-scroll'
          itemContent={(_, session) => (
            <SessionListItem
              key={generateSessionKey(session.SessionType, session.ID)}
              session={session}
              onLinkClick={isDesktop ? undefined : handleKeepListPosition}
            />
          )}
          data={availableSessions}
          defaultItemHeight={60}
          overscan={6}
          components={components}
          restoreStateFrom={lastPosition && typeof lastPosition !== 'number' ? lastPosition : undefined}
        />
      )}
    </LoadingLayout>
  );
}
